import { useMemo } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Button, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ClockCircleOutlined, DisconnectOutlined } from '@ant-design/icons'
import type { McpConnection } from '@/types/mcp'
import { endReason, endedAt, isExpiringSoon, orderConnections } from '../connections'

dayjs.extend(relativeTime)

const formatDate = (iso: string | null) => (iso && dayjs(iso).isValid() ? dayjs(iso).format('DD/MM/YYYY') : '-')

type ConnectionsTableProps = {
  connections: McpConnection[]
  loading: boolean
  showUser?: boolean
  showClient?: boolean
  emptyText: string
  disconnectingId?: string
  onDisconnect: (connection: McpConnection) => void
}

export default function ConnectionsTable({
  connections,
  loading,
  showUser = true,
  showClient = false,
  emptyText,
  disconnectingId,
  onDisconnect,
}: ConnectionsTableProps) {
  const rows = useMemo(() => orderConnections(connections), [connections])

  const columns: ColumnsType<McpConnection> = [
    ...(showUser ? [{ title: 'User', dataIndex: 'userName', key: 'user' }] : []),
    ...(showClient ? [{ title: 'Client', dataIndex: 'clientName', key: 'client' }] : []),
    {
      title: 'App',
      key: 'app',
      render: (_, row) => (
        <div>
          <div>{row.appName}</div>
          <div className="text-xs text-gray-500">{row.redirectHost}</div>
        </div>
      ),
    },
    { title: 'Connected', key: 'connected', render: (_, row) => formatDate(row.connectedAt) },
    {
      title: 'Last used',
      key: 'lastUsed',
      render: (_, row) =>
        row.lastUsedAt ? (
          <Tooltip title={dayjs(row.lastUsedAt).format('DD/MM/YYYY h:mm A')}>
            <span>{dayjs(row.lastUsedAt).fromNow()}</span>
          </Tooltip>
        ) : (
          <span className="text-gray-400">Not yet</span>
        ),
    },
    {
      title: 'Expires',
      key: 'expires',
      render: (_, row) => {
        if (!row.isLive) return <span className="text-gray-400">-</span>
        if (!isExpiringSoon(row)) return formatDate(row.expiresAt)
        return (
          <Tooltip title="Expires within 7 days">
            <span className="inline-flex items-center gap-1.5 font-medium text-amber-600">
              <ClockCircleOutlined />
              {formatDate(row.expiresAt)}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, row) =>
        row.isLive ? (
          <Tag color="green">Live</Tag>
        ) : (
          <div>
            <Tag>Ended</Tag>
            <div className="mt-1 text-xs text-gray-500">
              {endReason(row)}, {formatDate(endedAt(row))}
            </div>
          </div>
        ),
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      render: (_, row) =>
        row.isLive ? (
          <Button
            danger
            size="small"
            icon={<DisconnectOutlined />}
            loading={disconnectingId === row.connectionId}
            aria-label={`Disconnect ${row.appName || 'app'}${showUser ? ` for ${row.userName}` : ''}${showClient ? ` from ${row.clientName}` : ''}`}
            onClick={() => onDisconnect(row)}
          >
            Disconnect
          </Button>
        ) : null,
    },
  ]

  return (
    <Table<McpConnection>
      rowKey="connectionId"
      columns={columns}
      dataSource={rows}
      loading={loading}
      rowClassName={(row) => (row.isLive ? '' : 'text-gray-500')}
      locale={{ emptyText }}
      pagination={{ pageSize: 10, hideOnSinglePage: true }}
      scroll={{ x: 'max-content' }}
    />
  )
}
